import React, { createContext, useCallback, useEffect, useRef, useState } from 'react';
import * as authApi from '@/api/auth';
import { attemptTokenRefresh, onSessionExpired, resetSessionState } from '@/api/client';
import type { User, RegisterData, LoginData, ApiError } from '@/api/auth';

// T010: Refresh token 3 minutes before access token expires (assuming 15min access token)
// Increased from 2min buffer to 3min for more margin
const TOKEN_REFRESH_INTERVAL = 12 * 60 * 1000; // 12 minutes in ms

// T011: Threshold for "long inactivity" - if user was away longer than this, force refresh
const LONG_INACTIVITY_THRESHOLD = 10 * 60 * 1000; // 10 minutes in ms

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
  login: (data: LoginData) => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  clearError: () => void;
}

export const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const refreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const isAuthenticated = !!user;

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Proactive token refresh - refresh before expiration
  // Uses centralized attemptTokenRefresh to prevent concurrent refresh races
  const proactiveRefresh = useCallback(async () => {
    try {
      const refreshed = await attemptTokenRefresh();
      if (refreshed) {
        // Token refreshed successfully, fetch updated user info
        const currentUser = await authApi.getCurrentUser();
        setUser(currentUser);
      }
      // If refresh failed, onSessionExpired will handle logout
    } catch {
      // Ignore errors - session expired handler will take care of it
    }
  }, []);

  // Start proactive refresh interval
  const startRefreshInterval = useCallback(() => {
    // Clear any existing interval
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }
    // Set up new interval to refresh token periodically
    refreshIntervalRef.current = setInterval(proactiveRefresh, TOKEN_REFRESH_INTERVAL);
  }, [proactiveRefresh]);

  // Stop proactive refresh interval
  const stopRefreshInterval = useCallback(() => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }
  }, []);

  // Fetch current user (on mount and after login)
  const refreshUser = useCallback(async () => {
    try {
      const currentUser = await authApi.getCurrentUser();
      setUser(currentUser);
    } catch {
      setUser(null);
    }
  }, []);

  // Initial auth check with retry mechanism for multi-tab scenarios and server restarts
  useEffect(() => {
    const checkAuth = async (retryCount = 0) => {
      const MAX_RETRIES = 5; // Increased for server restart scenarios
      const RETRY_DELAY = 1000; // 1 second between retries

      // Helper to detect network errors (server not ready)
      const isNetworkError = (error: unknown): boolean => {
        if (error instanceof TypeError && error.message.includes('fetch')) return true;
        if (error instanceof Error && error.message.includes('NetworkError')) return true;
        if (error instanceof Error && error.message.includes('Failed to fetch')) return true;
        return false;
      };

      try {
        // Try to get current user with existing cookies
        const currentUser = await authApi.getCurrentUser();
        // Reset session state when authenticated
        resetSessionState();
        setUser(currentUser);
      } catch (error) {
        // Check if this is a network error (server might be restarting)
        if (isNetworkError(error) && retryCount < MAX_RETRIES) {
          console.log(`[Auth] Network error, server may be restarting - retrying (${retryCount + 1}/${MAX_RETRIES})...`);
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
          return checkAuth(retryCount + 1);
        }

        // Not authenticated or token expired - try to refresh
        try {
          const refreshed = await attemptTokenRefresh();
          if (refreshed) {
            // Wait a moment for cookies to propagate, then try to get user
            await new Promise(resolve => setTimeout(resolve, 100));
            const currentUser = await authApi.getCurrentUser();
            // Reset session state when refreshed successfully
            resetSessionState();
            setUser(currentUser);
          } else {
            // Refresh failed - but maybe another tab just refreshed and we have new cookies
            // Retry a few times before giving up (handles multi-tab race conditions)
            if (retryCount < MAX_RETRIES) {
              console.log(`[Auth] Initial check failed, retrying (${retryCount + 1}/${MAX_RETRIES})...`);
              await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
              return checkAuth(retryCount + 1);
            }
            // No valid session after retries
            setUser(null);
          }
        } catch (refreshError) {
          // Check if refresh failed due to network error
          if (isNetworkError(refreshError) && retryCount < MAX_RETRIES) {
            console.log(`[Auth] Network error during refresh, retrying (${retryCount + 1}/${MAX_RETRIES})...`);
            await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
            return checkAuth(retryCount + 1);
          }
          // No valid session
          setUser(null);
        }
      } finally {
        if (retryCount === 0 || retryCount >= MAX_RETRIES) {
          setIsLoading(false);
        }
      }
    };

    checkAuth();
  }, []);

  // Manage refresh interval based on authentication state
  useEffect(() => {
    if (isAuthenticated) {
      startRefreshInterval();
    } else {
      stopRefreshInterval();
    }

    // Cleanup on unmount
    return () => {
      stopRefreshInterval();
    };
  }, [isAuthenticated, startRefreshInterval, stopRefreshInterval]);

  // Subscribe to session expired events from the centralized client
  // This handles the case where refresh fails definitively
  useEffect(() => {
    const unsubscribe = onSessionExpired(() => {
      console.log('Session expired - logging out');
      stopRefreshInterval();
      setUser(null);
    });

    return unsubscribe;
  }, [stopRefreshInterval]);

  // T011: Refresh token when tab becomes visible again (handles PC sleep/background tabs)
  // Uses centralized attemptTokenRefresh to prevent concurrent refresh races
  // Only refresh if tab was hidden for at least MIN_HIDDEN_TIME to avoid race conditions
  // when switching between tabs rapidly
  useEffect(() => {
    if (!isAuthenticated) return;

    let lastHiddenTime = 0;
    const MIN_HIDDEN_TIME = 30 * 1000; // 30 seconds - don't refresh for quick tab switches

    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'hidden') {
        // Track when tab became hidden
        lastHiddenTime = Date.now();
      } else if (document.visibilityState === 'visible') {
        // Tab is now visible - check how long we were away
        const timeAway = lastHiddenTime > 0 ? Date.now() - lastHiddenTime : 0;

        // Skip refresh for quick tab switches (< 30 seconds)
        // This prevents race conditions when opening multiple tabs
        if (timeAway < MIN_HIDDEN_TIME) {
          console.log(`[Auth] Tab was hidden for only ${Math.round(timeAway / 1000)}s - skipping refresh`);
          return;
        }

        const forceRefresh = timeAway > LONG_INACTIVITY_THRESHOLD;

        if (forceRefresh) {
          console.log(`[Auth] Tab was hidden for ${Math.round(timeAway / 1000)}s - forcing token refresh`);
        } else {
          console.log(`[Auth] Tab was hidden for ${Math.round(timeAway / 1000)}s - attempting normal refresh`);
        }

        try {
          // T012: Use forceBypassDebounce when returning from long inactivity
          const refreshed = await attemptTokenRefresh(forceRefresh);
          if (refreshed) {
            const currentUser = await authApi.getCurrentUser();
            setUser(currentUser);
          }
          // If refresh failed, onSessionExpired will handle logout
        } catch {
          // Ignore errors - session expired handler will take care of it
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated]);

  // Login
  const login = useCallback(async (data: LoginData) => {
    setError(null);
    setIsLoading(true);

    try {
      const response = await authApi.login(data);
      // Reset session state when user logs in successfully
      resetSessionState();
      setUser(response.user);
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Login failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Register
  const register = useCallback(async (data: RegisterData) => {
    setError(null);
    setIsLoading(true);

    try {
      const response = await authApi.register(data);
      // Reset session state when user registers successfully
      resetSessionState();
      setUser(response.user);
    } catch (err) {
      const apiError = err as ApiError;
      setError(apiError.message || 'Registration failed');
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Logout
  const logout = useCallback(async () => {
    setIsLoading(true);
    stopRefreshInterval();

    try {
      await authApi.logout();
    } catch {
      // Ignore logout errors
    } finally {
      setUser(null);
      setIsLoading(false);
    }
  }, [stopRefreshInterval]);

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated,
    error,
    login,
    register,
    logout,
    refreshUser,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
